import React from 'react';
import { Heart, Leaf, Sparkles, Globe } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';

const AboutPage = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Our Story - Honouring Ancient African Wellness | The Perfect Peach Club</title>
        <meta name="description" content="Learn about our mission to support women with intentional intimate care, honouring ancient African feminine wellness traditions with modern, informed products." />
        <meta name="keywords" content="African wellness traditions, intimate care philosophy, conscious self-care for women, intentional wellness, feminine wellness heritage" />
      </Helmet>
      
      <Navigation />
      <ChatWidget />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/91edb9d9-5d41-4003-a549-830c6def8bd5/images/f778239db2dc49357dc058c53157cae51c9272f6439af3d1b0431741fe7b3f5a.png"
            alt="Terracotta bath with milk and herbs - African wellness ritual"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-bone-white/40 via-bone-white/60 to-bone-white" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-playfair text-5xl sm:text-6xl font-bold text-[#2D2420] mb-6">
            Our Story
          </h1>
          <p className="text-xl text-african-earth leading-relaxed">
            Honoring ancient African feminine hygiene practices while empowering modern wellness
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-playfair text-4xl font-bold text-[#2D2420] mb-6">
                Rooted in Tradition, Blooming in Wellness
              </h2>
              <p className="text-african-earth text-lg leading-relaxed mb-6">
                The Perfect Peach Club was born from a deep respect for the ancient wisdom passed down
                through generations of African women. Our ancestors understood the delicate balance of
                feminine wellness long before modern science caught up.
              </p>
              <p className="text-african-earth text-lg leading-relaxed mb-6">
                We invite time-tested botanical knowledge to meet contemporary wellness routines, creating
                products designed to honour tradition while supporting modern life. Every ingredient
                is chosen for its historical significance in African intimate care practices.
              </p>
              <p className="text-african-earth text-lg leading-relaxed">
                Our mission is simple: to support every woman with natural, confidence-building products
                that honour her body's inherent wisdom and invite balance.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_91edb9d9-5d41-4003-a549-830c6def8bd5/artifacts/o27rvoil_product_spin_video_1f0fd2a3-83fe-6290-82e7-f002d10fc738_0_0.png"
                alt="The Perfect Peach Club Hydra Oil"
                className="rounded-2xl shadow-soft w-full h-64 object-cover"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_91edb9d9-5d41-4003-a549-830c6def8bd5/artifacts/41lsdd11_athome_product_shoot_1f0fd289-7196-6b30-ad99-03526b8fe0c0_0_0%20%281%29.png"
                alt="The Perfect Peach Club products"
                className="rounded-2xl shadow-soft w-full h-64 object-cover mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-playfair text-4xl font-bold text-[#2D2420] text-center mb-12">
            Our Core Values
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Leaf className="w-10 h-10" />,
                title: 'Natural Purity',
                description: 'Only the finest botanical ingredients, sourced with respect for nature and tradition.',
              },
              {
                icon: <Heart className="w-10 h-10" />,
                title: 'Empowerment',
                description: 'Building confidence through education, tradition, and self-care.',
              },
              {
                icon: <Sparkles className="w-10 h-10" />,
                title: 'Ancient Wisdom',
                description: 'Honoring the knowledge passed down through generations of African women.',
              },
              {
                icon: <Globe className="w-10 h-10" />,
                title: 'Community',
                description: 'Creating a supportive space for women to share and grow together.',
              },
            ].map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-8 shadow-soft hover:shadow-floating transition-all duration-500 text-center group"
              >
                <div className="text-perfect-peach mb-4 transform group-hover:scale-110 transition-transform duration-300 flex justify-center">
                  {value.icon}
                </div>
                <h3 className="font-playfair text-xl font-semibold text-[#2D2420] mb-3">
                  {value.title}
                </h3>
                <p className="text-african-earth leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Heritage Section */}
      <section className="py-20 bg-gradient-to-br from-perfect-peach to-royal-gold">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-playfair text-4xl sm:text-5xl font-bold text-white mb-6">
            Celebrating African Feminine Heritage
          </h2>
          <p className="text-white/90 text-lg leading-relaxed mb-8">
            For centuries, African women have used natural botanicals for feminine wellness.
            From the baobab trees of the savanna to the rooibos of the Cape, our continent
            has always provided the answers to our wellness needs. We're proud to continue
            this legacy, bringing ancient practices into the modern world with respect,
            authenticity, and love.
          </p>
          <p className="text-white font-playfair text-2xl italic">
            "Your body is a temple. Honor it with nature's wisdom."
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;